import React, { useState, useRef, useEffect } from 'react';
import { usePanelLinkContext, LINK_COLORS, LINK_COLOR_VALUES, LinkColor } from '../../contexts/PanelLinkContext';

interface ColorLinkDotProps {
  panelId: string;
  size?: 'sm' | 'md';
}

export const ColorLinkDot: React.FC<ColorLinkDotProps> = ({ panelId, size = 'sm' }) => {
  const { getPanelColor, cyclePanelColor, setPanelColor, getPanelsByColor } = usePanelLinkContext();
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const currentColor = getPanelColor(panelId);
  const isLinked = currentColor !== 'none';
  const linkedPanelsCount = getPanelsByColor(currentColor).length;

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Shift+click opens picker
    if (e.shiftKey) {
      setShowPicker(!showPicker);
    } else {
      // Normal click cycles colors
      cyclePanelColor(panelId);
    }
  };

  const handleColorSelect = (color: LinkColor) => {
    setPanelColor(panelId, color);
    setShowPicker(false);
  };

  const sizeClasses = {
    sm: 'w-2 h-2 md:w-1.5 md:h-1.5',
    md: 'w-3 h-3 md:w-2 md:h-2',
  };

  const buttonSizeClasses = {
    sm: 'w-6 h-6 md:w-4 md:h-4',
    md: 'w-8 h-8 md:w-5 md:h-5',
  };

  return (
    <div className="relative" ref={pickerRef}>
      {/* Dot Button */}
      <button
        onClick={handleClick}
        className={`${buttonSizeClasses[size]} flex items-center justify-center rounded hover:bg-terminal-border/50 transition-colors group`}
        title={`Link panel (click to change, shift+click for picker)${isLinked ? ` - ${linkedPanelsCount} linked` : ''}`}
      >
        <span
          className={`${sizeClasses[size]} rounded-full transition-all ${
            isLinked ? 'animate-pulse' : ''
          } ${currentColor === 'none' ? 'border border-gray-600' : ''}`}
          style={{
            backgroundColor: currentColor !== 'none' ? LINK_COLOR_VALUES[currentColor] : 'transparent',
            boxShadow: isLinked ? `0 0 4px ${LINK_COLOR_VALUES[currentColor]}` : 'none',
          }}
        />
      </button>

      {/* Color Picker Dropdown */}
      {showPicker && (
        <div className="absolute top-full left-0 mt-1 p-1.5 bg-terminal-panel border border-terminal-border rounded shadow-lg z-50 flex gap-1.5">
          {LINK_COLORS.map(color => {
            const count = getPanelsByColor(color).length;
            const isSelected = color === currentColor;

            return (
              <button
                key={color}
                onClick={() => handleColorSelect(color)}
                className={`w-5 h-5 rounded flex items-center justify-center transition-all hover:scale-110 ${
                  isSelected ? 'ring-1 ring-white ring-offset-1 ring-offset-terminal-panel' : ''
                }`}
                title={color === 'none' ? 'Unlinked' : `${color} (${count} panels)`}
              >
                <span
                  className={`w-3 h-3 rounded-full ${color === 'none' ? 'border border-gray-600' : ''}`}
                  style={{
                    backgroundColor: color !== 'none' ? LINK_COLOR_VALUES[color] : 'transparent',
                  }}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ColorLinkDot;
